import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { UserPlus } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data.users)).catch(() => {});
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      await api.post("/admin/users/invite", { email, role: "DONATION_VIEWER" });
      setState({ status: "success", message: `Invitation sent to ${email}. The link works once and expires in 48 hours.` });
      setEmail("");
      load();
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  const act = async (u, action) => {
    const labels = { suspend: "Suspend", reactivate: "Reactivate", remove: "Permanently remove" };
    if (!window.confirm(`${labels[action]} ${u.email}?`)) return;
    try {
      if (action === "remove") await api.delete(`/admin/users/${u.id}`);
      else await api.post(`/admin/users/${u.id}/${action}`);
      load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-users">
      <h1 className="font-serif text-3xl font-semibold text-forest">User management</h1>
      <p className="mt-2 text-sm text-charcoal/70 max-w-2xl leading-relaxed">
        Administration is invitation-only. Invited administrators can publish news, answer
        enquiries, send newsletters and view donation reporting. User management, settings,
        audit logs and donation imports remain restricted to you.
      </p>

      <form onSubmit={invite} className="mt-8 bg-white border border-sandstone p-6 max-w-2xl" data-testid="invite-form">
        <h2 className="font-serif text-xl font-semibold text-forest flex items-center gap-2">
          <UserPlus size={18} aria-hidden="true" /> Add administrator
        </h2>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <label htmlFor="invite-email" className="sr-only">Administrator email</label>
          <input id="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="administrator@example.org" data-testid="invite-email-input"
            className="flex-1 border border-sage bg-white px-4 py-2.5 text-sm rounded-sm focus:border-forest focus:ring-1 focus:ring-forest" />
          <button type="submit" disabled={state.status === "loading"} data-testid="invite-submit-button"
            className="bg-forest text-white text-sm font-medium px-6 py-2.5 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
            {state.status === "loading" ? "Sending…" : "Send invitation"}
          </button>
        </div>
        <p className="mt-2 text-xs text-charcoal/60">Role: Administrator (news, enquiries, newsletters, donation reporting).</p>
        {state.status === "success" && <p className="mt-3 text-sm text-forest" data-testid="invite-success">{state.message}</p>}
        {state.status === "error" && <p className="mt-3 text-sm text-red-700" role="alert" data-testid="invite-error">{state.message}</p>}
      </form>

      <div className="mt-8 bg-white border border-sandstone overflow-x-auto max-w-4xl">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left border-b border-sandstone text-xs uppercase tracking-wider text-charcoal/60">
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Role</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-sandstone/60 last:border-0">
                <td className="px-5 py-3">{u.email}</td>
                <td className="px-5 py-3">{u.role === "SUPER_ADMIN" ? "Super Administrator" : "Administrator"}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${u.status === "active" ? "text-forest" : u.status === "invited" ? "text-gold-dark" : "text-red-700"}`}>
                    {u.status}
                  </span>
                </td>
                <td className="px-5 py-3">
                  {u.role !== "SUPER_ADMIN" && (
                    <div className="flex gap-2 justify-end">
                      {u.status !== "suspended" ? (
                        <button onClick={() => act(u, "suspend")} data-testid={`user-suspend-${u.id}`}
                          className="text-xs border border-sage px-3 py-1.5 hover:bg-sandstone transition-colors duration-300">Suspend</button>
                      ) : (
                        <button onClick={() => act(u, "reactivate")} data-testid={`user-reactivate-${u.id}`}
                          className="text-xs border border-sage px-3 py-1.5 hover:bg-sandstone transition-colors duration-300">Reactivate</button>
                      )}
                      <button onClick={() => act(u, "remove")} data-testid={`user-remove-${u.id}`}
                        className="text-xs border border-red-300 text-red-700 px-3 py-1.5 hover:bg-red-50 transition-colors duration-300">Remove</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
